import React, { useEffect, useState, useCallback } from "react";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";
import useTranslation from "next-translate/useTranslation";

import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import IconButton from "@material-ui/core/IconButton";
import MenuItem from "@material-ui/core/MenuItem";
import Menu from "@material-ui/core/Menu";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import useScrollTrigger from "@material-ui/core/useScrollTrigger";

import MenuIcon from "@material-ui/icons/Menu";
import AccountCircle from "@material-ui/icons/AccountCircle";
import TranslateIcon from "@material-ui/icons/Translate";
import Link from "next/link";
import toLower from "lodash/toLower";
import { useRouter } from "next/router";
import { useSession, signOut } from "next-auth/react";
import useCompleoCareerPageContext from "../../useCompleoCareerPageContext";
import Logo from "../../../../components/Logo";
import { getUniqueLinkFromDomain } from "functions/util";
import Loading from "components/Loading";
import ChangeLanguage from "../ChangeLanguage";
interface IElevationScrollProps {
    window?: () => Window;
    children: React.ReactElement;
    elevationScrollThreshold: number | undefined;
    classes: any;
}

interface IHeaderProps {
    elevationScrollThreshold?: number | undefined;
    name: string;
    metadata: any;
    loginEnabled?: boolean;
    careerPageClasses: any;
}

function ElevationScroll(props: IElevationScrollProps) {
    const { children, window, elevationScrollThreshold, classes } = props;

    const scrollTrigger = useScrollTrigger({
        disableHysteresis: true,
        threshold: elevationScrollThreshold,
        target: window ? window() : undefined,
    });

    const trigger = elevationScrollThreshold === undefined || scrollTrigger;

    return React.cloneElement(children, {
        className: trigger ? classes.rootWithColor : classes.root,
        elevation: trigger ? 4 : 0,
    });
}

export const Header = ({
    elevationScrollThreshold,
    name,
    metadata,
    loginEnabled,
}: IHeaderProps) => {
    const [data] = useCompleoCareerPageContext();
    const {
        uniqueLink,
        moduleName,
        isCustomDomain,
        language,
        supportedLanguages,
    } = data;
    // const [isCustomDomain, setIsCustomDomain] = useState(false);
    // const [finished, setFinished] = useState(false);

    const { t } = useTranslation(moduleName);

    const items: any[] = metadata.items;
    const logoElementItem = items.find(
        (item: any) => item.elementType === "logoImage"
    );

    const barStyle = metadata.customStyle?.filter(
        (fc: any) => fc.name === "bar"
    )[0];

    const menuStyle = metadata.customStyle?.filter(
        (fc: any) => fc.name === "menu"
    )[0];

    const useStyles = makeStyles((theme: Theme) =>
        createStyles({
            root: {
                backgroundColor:
                    elevationScrollThreshold &&
                    (!barStyle?.backgroundColor ||
                        barStyle.backgroundColor === theme.palette.primary.main)
                        ? undefined
                        : barStyle?.backgroundColor ||
                          theme.palette.primary.main,
                position: "fixed",
                right: 0,
                left: 0,
                zIndex: 1030,
                top: 0,
                flexWrap: "nowrap",
                flexFlow: "row nowrap",
                display: "flex",
                alignItems: "center",
                "&:before":
                    elevationScrollThreshold &&
                    (!barStyle?.backgroundColor ||
                        barStyle.backgroundColor === theme.palette.primary.main)
                        ? {
                              content: '""',
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              backgroundImage:
                                  "linear-gradient(-180deg,#231f1f,rgba(35,31,31,0) 90%)",
                              opacity: ".4",
                          }
                        : undefined,
            },
            rootWithColor: {
                backgroundColor:
                    barStyle?.backgroundColor || theme.palette.primary.main,
                position: "fixed",
                right: 0,
                left: 0,
                zIndex: 1030,
                top: 0,
                flexWrap: "nowrap",
                flexFlow: "row nowrap",
                display: "flex",
                alignItems: "center",
                "&:before":
                    elevationScrollThreshold &&
                    (!barStyle?.backgroundColor ||
                        barStyle.backgroundColor === theme.palette.primary.main)
                        ? {
                              content: '""',
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              backgroundImage:
                                  "linear-gradient(-180deg,#231f1f,rgba(35,31,31,0) 90%)",
                              opacity: ".4",
                          }
                        : undefined,
            },
            toolbar: {
                width: "100%",
                [theme.breakpoints.up("sm")]: {
                    minHeight: "56px!important",
                },
                paddingRight:
                    loginEnabled && elevationScrollThreshold
                        ? "0px!important"
                        : "24px",
            },
            grow: {
                flexGrow: 1,
            },
            menuButton: {
                marginRight: theme.spacing(2),
            },
            title: {
                display: "none",
                [theme.breakpoints.up("sm")]: {
                    display: "block",
                },
            },
            buttonRoot: {
                color:
                    menuStyle?.fontColor || theme.palette.primary.contrastText,
                fontSize:
                    menuStyle?.fontSize || theme.typography.button.fontSize,
                marginRight: theme.spacing(4),
                whiteSpace: "nowrap",
            },
            buttonIcon: {
                color: menuStyle?.fontColor || theme.palette.common.white,
            },
            logo: {
                maxWidth: "200px",
                maxHeight: "48px",
                width: "200px",
                height: "48px",
                position: "relative",
                "& img": {
                    cursor: "pointer",
                    filter: logoElementItem?.keepOriginalColor
                        ? undefined
                        : "brightness(0) saturate(100%) invert(100%) sepia(0%) hue-rotate(268deg) contrast(101%)",
                },
            },
            sectionDesktop: {
                display: "none",
                paddingRight: "10px",
                [theme.breakpoints.up("md")]: {
                    display: "flex",
                    flexWrap: "nowrap",
                },
            },
            sectionMobile: {
                display: "flex",
                [theme.breakpoints.up("md")]: {
                    display: "none",
                },
            },
            paper: {
                position: "absolute",
                width: 400,
                backgroundColor: theme.palette.background.paper,
                border: "2px solid #000",
                boxShadow: theme.shadows[5],
                padding: theme.spacing(2, 4, 3),
            },
        })
    );

    const classes = useStyles();
    const router = useRouter();
    const { data: session } = useSession();

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [mobileMoreAnchorEl, setMobileMoreAnchorEl] =
        React.useState<null | HTMLElement>(null);

    const [menuElementLanguage, setMenuElementLanguage] =
        React.useState<HTMLElement | null>(null);

    const isMenuOpen = Boolean(anchorEl);
    const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

    const menuItems = items
        .filter((item: any) => item.elementType === "menu")[0]
        .items.map((menuItem: any) => menuItem);
    const menuItemElementName = items.filter(
        (item: any) => item.elementType === "menu"
    )[0].elementName;

    const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleLanguageMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setMenuElementLanguage(event.currentTarget);
    };

    const handleLanguageMenuClose = () => {
        setMenuElementLanguage(null);
    };

    const handleMobileMenuClose = () => {
        setMobileMoreAnchorEl(null);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        handleMobileMenuClose();
    };

    const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setMobileMoreAnchorEl(event.currentTarget);
    };

    const handleLogoff = async () => {
        const loginPath = isCustomDomain
            ? "/account/login"
            : `/${uniqueLink.toLowerCase()}/account/login`;
        const redirectTo = router.asPath;
        const data = await signOut({
            redirect: false,
            callbackUrl: `${loginPath}?to=${redirectTo}`,
        });
        if (data.url) {
            router.push(data.url);
        }
    };

    const handleLogin = () => {
        const loginPath = isCustomDomain
            ? "/account/login"
            : `/${uniqueLink.toLowerCase()}/account/login`;
        const redirectTo = router.asPath;
        router.push({
            pathname: loginPath,
            query: { to: redirectTo },
        });
    };

    const menuId = "primary-search-account-menu";

    const renderMenu = loginEnabled ? (
        <Menu
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            id={menuId}
            keepMounted
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            open={isMenuOpen}
            onClose={handleMenuClose}
        >
            {session && <MenuItem onClick={handleLogoff}>Logoff</MenuItem>}
            {!session && <MenuItem onClick={handleLogin}>Login</MenuItem>}
        </Menu>
    ) : null;

    const mobileMenuId = "primary-search-account-menu-mobile";
    const renderMobileMenu = (
        <Menu
            anchorEl={mobileMoreAnchorEl}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            id={mobileMenuId}
            keepMounted
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            open={isMobileMenuOpen}
            onClose={handleMobileMenuClose}
        >
            {menuItems.map((menuItem: any) => {
                if (menuItem.href.includes("http")) {
                    return (
                        <MenuItem key={menuItem.name}>
                            <a
                                href={menuItem.href}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {t(
                                    `${name}_${menuItemElementName}_${menuItem.name}`
                                )}
                            </a>
                        </MenuItem>
                    );
                } else {
                    return (
                        <MenuItem key={menuItem.name}>
                            <Link
                                href={
                                    isCustomDomain
                                        ? menuItem.href
                                        : `/${toLower(uniqueLink)}${
                                              menuItem.href
                                          }`
                                }
                            >
                                {t(
                                    `${name}_${menuItemElementName}_${menuItem.name}`
                                )}
                            </Link>
                        </MenuItem>
                    );
                }
            })}
        </Menu>
    );

    return (
        <div>
            <header>
                <ElevationScroll
                    elevationScrollThreshold={elevationScrollThreshold}
                    classes={classes}
                >
                    <AppBar
                        position="static"
                        color="transparent"
                        elevation={0}
                        className={classes.root}
                    >
                        <Toolbar className={classes.toolbar}>
                            {items.map((item: any) => {
                                switch (item.elementType) {
                                    case "logoImage":
                                        return (
                                            <Logo
                                                key={item.elementName}
                                                className={classes.logo}
                                                imageUrl={item.srcUrl}
                                                href={
                                                    item.href?.includes("http")
                                                        ? item.href
                                                        : isCustomDomain
                                                        ? item.href || "/home"
                                                        : `/${toLower(
                                                              uniqueLink
                                                          )}${
                                                              item.href ||
                                                              "/home"
                                                          }`
                                                }
                                            />
                                        );
                                    case "menu":
                                        return (
                                            <React.Fragment
                                                key={item.elementName}
                                            >
                                                <div className={classes.grow} />
                                                <div
                                                    className={
                                                        classes.sectionDesktop
                                                    }
                                                >
                                                    <Grid
                                                        container
                                                        direction="row"
                                                        justify="flex-end"
                                                        wrap="nowrap"
                                                        spacing={
                                                            elevationScrollThreshold
                                                                ? 1
                                                                : undefined
                                                        }
                                                        alignItems="center"
                                                    >
                                                        {item.items.map(
                                                            (menuItem: any) => {
                                                                const localRedirectTo =
                                                                    isCustomDomain
                                                                        ? menuItem.href
                                                                        : `/${toLower(
                                                                              uniqueLink
                                                                          )}${
                                                                              menuItem.href
                                                                          }`;

                                                                return (
                                                                    <Grid
                                                                        key={
                                                                            menuItem.name
                                                                        }
                                                                        item
                                                                    >
                                                                        {menuItem.href.includes(
                                                                            "http"
                                                                        ) ? (
                                                                            <a
                                                                                href={
                                                                                    menuItem.href
                                                                                }
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                style={{
                                                                                    textDecoration:
                                                                                        "none",
                                                                                }}
                                                                            >
                                                                                <Button
                                                                                    aria-controls="simple-menu"
                                                                                    aria-haspopup="false"
                                                                                    classes={{
                                                                                        root: classes.buttonRoot,
                                                                                    }}
                                                                                >
                                                                                    {t(
                                                                                        `${name}_${item.elementName}_${menuItem.name}`
                                                                                    )}
                                                                                </Button>
                                                                            </a>
                                                                        ) : (
                                                                            <Link
                                                                                href={
                                                                                    localRedirectTo
                                                                                }
                                                                                passHref
                                                                            >
                                                                                <Button
                                                                                    aria-controls="simple-menu"
                                                                                    aria-haspopup="false"
                                                                                    classes={{
                                                                                        root: classes.buttonRoot,
                                                                                    }}
                                                                                >
                                                                                    {t(
                                                                                        `${name}_${item.elementName}_${menuItem.name}`
                                                                                    )}
                                                                                </Button>
                                                                            </Link>
                                                                        )}
                                                                    </Grid>
                                                                );
                                                            }
                                                        )}
                                                        {(
                                                            supportedLanguages ||
                                                            []
                                                        ).length > 1 && (
                                                            <Grid item>
                                                                <IconButton
                                                                    aria-label="change language"
                                                                    className={
                                                                        classes.buttonIcon
                                                                    }
                                                                    onClick={
                                                                        handleLanguageMenuOpen
                                                                    }
                                                                >
                                                                    <TranslateIcon />
                                                                </IconButton>
                                                            </Grid>
                                                        )}
                                                        <Grid item>
                                                            <IconButton
                                                                edge="end"
                                                                aria-label="account of current user"
                                                                aria-controls={
                                                                    menuId
                                                                }
                                                                aria-haspopup="true"
                                                                onClick={
                                                                    handleProfileMenuOpen
                                                                }
                                                                className={
                                                                    classes.buttonIcon
                                                                }
                                                            >
                                                                <AccountCircle />
                                                            </IconButton>
                                                        </Grid>
                                                    </Grid>
                                                </div>
                                                {/* <IconButton
                                                    onClick={
                                                        handleHeaderEditorOpen
                                                    }
                                                    className={
                                                        careerPageClasses.editorButton
                                                    }
                                                >
                                                    <CreateIcon />
                                                </IconButton> */}
                                            </React.Fragment>
                                        );
                                }
                            })}

                            <div className={classes.sectionMobile}>
                                <IconButton
                                    aria-label="show more"
                                    aria-controls={mobileMenuId}
                                    aria-haspopup="true"
                                    onClick={handleMobileMenuOpen}
                                    className={classes.buttonIcon}
                                >
                                    <MenuIcon />
                                </IconButton>
                            </div>
                        </Toolbar>
                    </AppBar>
                </ElevationScroll>
            </header>
            {renderMobileMenu}
            {renderMenu}
            {supportedLanguages?.length > 1 && (
                <ChangeLanguage
                    anchorEl={menuElementLanguage}
                    closeMenu={handleLanguageMenuClose}
                    languageRendered={language}
                    supportedLanguages={supportedLanguages}
                />
            )}
        </div>
    );
};

export default Header;
